/* ========================================================================
 * Apricot's Local Navigation Mega Menu
 * ======================================================================== */

// SCSS
import '../scss/includes/apricot-base.scss';
import '../scss/includes/local-navigation-mega.scss';

// javaScript
import Utils from './CBUtils';

/**
 * MegaMenu
 *
 * @param {Object}   data
 * @param {Element}  data.elem             Nav root element to scope to.
 * @param {Boolean}  [data.sticky]         Stick the nav to the top of the viewport on scroll. Default: true.
 * @param {Boolean}  [data.mobileLayout]   Enable the mobile hamburger / slide-panel layout at narrow viewports. Default: true.
 *                                         When false the desktop layout is used at all breakpoints.
 * @param {Boolean}  [data.mobileEscClose]       Pressing Escape closes the open mobile nav panel. Default: true.
 * @param {Boolean}  [data.mobileInteractionClose] Close mobile nav when user clicks a leaf link inside a panel. Useful for SPAs. Default: false.
 * @param {string}   [data.mobileLayoutBreakpoint] Breakpoint at which the mobile layout activates. 'xs' = 768px, 'sm' = 1024px. Default: 'xs'.
 * @param {string}   [data.selectedItem]            href of the initially-selected nav link. The plugin marks matching links with `cb-is-selected` and `aria-current="page"`. Can also be set at any time by calling `.setSelected(href)`.
 * @param {Boolean}  [data.dropdownCloseOnClick] Close the open panel when user clicks a link inside it. Default: true.
 * @param {string}   [data.landingPageSuffix]    Suffix appended to the section label for auto-injected landing-page links (e.g. "Home" → "Students Home"). Default: 'Home'.
 * @param {Function} data.callBack
 * @param {Function} data.callBackMobile
 * @param {Function} data.callBackDesktop
 * @param {Function} data.callBackMore
 * @param {Function} data.callBackMobileOpen
 * @param {Function} data.callBackMobileClose
 * @returns {{ destroy: Function, setSelected: Function }}
 */

const LocalNavigationMegaMenu = (data = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { destroy: () => {} };
  }

  const defaultData = {
    elem: null,
    sticky: true,
    mobileLayout: true,
    mobileEscClose: true,
    mobileInteractionClose: false,
    mobileLayoutBreakpoint: 'xs',
    selectedItem: null,
    dropdownCloseOnClick: true,
    landingPageSuffix: 'Home',
    callBack: null,
    callBackMobile: null,
    callBackDesktop: null,
    callBackMore: null,
    callBackMobileOpen: null,
    callBackMobileClose: null,
  };

  data = {
    ...defaultData,
    ...data,
  };

  const elem = data.elem;
  if (!elem || !(elem instanceof HTMLElement)) return { destroy: () => {} };

  // ── Constants ───────────────────────────────────────────────────────────────
  // TBD: remove hardcoded breakpoint values in favor of CSS custom properties set via media queries, similar to how widths are measured.
  const MOBILE_BP = data.mobileLayoutBreakpoint === 'sm' ? 1024 : 768; // px — breakpoint where mobile layout activates
  const RESIZE_DEBOUNCE_MS = 200; // ms — debounce for resize / overflow tracking
  const FOCUS_DELAY_MS = 50; // ms — delay before shifting focus into a panel
  const PANEL_CLEANUP_MS = 400; // ms — wait before removing a closed child panel (matches CSS transition)

  // overflow "More" state
  let navListElem = null;
  let moreItem = null;
  let moreTrigger = null;
  let moreItemWidth = 0; // mWidth — outer width of the More button
  let sWidth = 0; // outer width of the brand element
  let firstOverflowRun = true; // true on first trackOverflow run
  let resizeId = 0;
  let moreGrid = null; // the flex-grid div inside the More panel
  let overflowObserver = null; // ResizeObserver watching the inner container
  let measuringPanel = false; // true while positionPanel measures off-screen
  let navContainer = null; // .cb-local-navigation-mega wrapper

  // selected-item state
  let selectedHref = data.selectedItem || null;

  // mobile state
  let mobileTrigger = null;
  let mobileNav = null;
  let mobilePanelStack = []; // stack of currently-shown panel elements
  let mobileResizeId = 0;
  let lastInputType = 'mouse'; // 'mouse' | 'keyboard'

  const uniqueID = () => Utils.uniqueID(5, 'apricot_');

  // Recursively checks whether any item (or descendant) in a descriptor tree has the given href
  const containsHref = (items, href) =>
    items.some(item => item.href === href || (item.children && containsHref(item.children, href)));

  // ── Selection ────────────────────────────────────────────────────────────────
  // selectedHref is the single source of truth.
  // applySelection() does a full atomic sweep of every DOM surface that currently
  // exists (desktop links + any mobile panels in the DOM) and ensures exactly one
  // leaf link is marked, plus every ancestor in both trees.
  // setSelected() updates selectedHref then calls applySelection().

  const applySelection = () => {
    // ── 1. Clear desktop ────────────────────────────────────────────────────
    elem.querySelectorAll('.cb-mega-menu-link, .cb-menu-link').forEach(link => {
      link.classList.remove('cb-is-selected');

      link.removeAttribute('aria-current');
    });

    elem.querySelectorAll('.cb-desktop-navigation-trigger').forEach(t => {
      t.classList.remove('cb-is-selected');
    });

    // ── 2. Clear mobile ─────────────────────────────────────────────────────
    if (mobileNav) {
      mobileNav.querySelectorAll('.cb-site-mobile-panel-link').forEach(link => {
        link.classList.remove('cb-is-selected');

        link.removeAttribute('aria-current');
      });
    }

    if (!selectedHref) return;

    // ── 3. Mark desktop leaf link ───────────────────────────────────────────
    // Mega-menu panels are [hidden] when closed but are still in the DOM, so
    // querySelectorAll reaches them — no need to skip hidden panels.
    let desktopLeaf = null;
    elem.querySelectorAll('.cb-mega-menu-link, .cb-menu-link').forEach(link => {
      if (link.getAttribute('href') === selectedHref && !link.closest('.cb-mega-menu-link-promo')) {
        link.classList.add('cb-is-selected');

        link.setAttribute('aria-current', 'page');
        if (!desktopLeaf) desktopLeaf = link;
      }
    });

    // ── 4. Mark parent desktop trigger ─────────────────────────────────────
    if (desktopLeaf && desktopLeaf.classList.contains('cb-mega-menu-link')) {
      const panel = desktopLeaf.closest('.cb-mega-menu');
      if (panel) {
        const trigger = elem.querySelector(`.cb-desktop-navigation-trigger[aria-controls="${panel.id}"]`);
        if (trigger) trigger.classList.add('cb-is-selected');
      }
    }

    // ── 4b. If selected link is inside a More accordion panel, open it ──────
    // desktopLeaf may point to the original hidden panel link (earlier in DOM
    // order), not the cloned copy inside the More grid, so search moreGrid
    // directly for the matching href.
    if (moreGrid) {
      let moreAccLink = null;
      moreGrid.querySelectorAll('.cb-mega-more-accordion-panel .cb-mega-menu-link').forEach(link => {
        if (!moreAccLink && link.getAttribute('href') === selectedHref) moreAccLink = link;
      });
      if (moreAccLink) {
        const accPanel = moreAccLink.closest('.cb-mega-more-accordion-panel');
        if (accPanel) {
          accPanel.removeAttribute('hidden');
          const accTrigger = moreGrid.querySelector(`.cb-mega-more-accordion-trigger[aria-controls="${accPanel.id}"]`);
          if (accTrigger) accTrigger.setAttribute('aria-expanded', 'true');
        }
      }
    }

    if (!mobileNav) return;

    // ── 5a. Mark mobile leaf link + walk open ancestor panels ───────────────
    // Handles the case where the user has already drilled into a child panel.
    mobileNav
      .querySelectorAll('.cb-site-mobile-panel-link:not(.cb-site-mobile-panel-link-has-children)')
      .forEach(link => {
        if (link.getAttribute('href') !== selectedHref) return;
        link.classList.add('cb-is-selected');

        link.setAttribute('aria-current', 'page');
        let panel = link.closest('.cb-site-mobile-panel');
        while (panel && panel._parentLink) {
          panel._parentLink.classList.add('cb-is-selected');

          panel = panel._parentLink.closest('.cb-site-mobile-panel');
        }
      });

    // ── 5b. Mark has-children drill-in links whose subtree contains the href ─
    // Each drill-in link stores _navItemChildren at build time, so we can
    // evaluate containsHref without needing the child panel to be open.
    mobileNav.querySelectorAll('.cb-site-mobile-panel-link-has-children').forEach(link => {
      if (link._navItemChildren && containsHref(link._navItemChildren, selectedHref)) {
        link.classList.add('cb-is-selected');
      }
    });
  };

  const setSelected = target => {
    selectedHref = target instanceof Element ? target.getAttribute('href') : target;

    applySelection();
  };

  // ── Panel helpers ───────────────────────────────────────────────────────────

  const panelFor = trigger => {
    const id = trigger.getAttribute('aria-controls');

    return id ? elem.querySelector(`[id="${id}"]`) : null;
  };

  const isOpen = trigger => trigger.getAttribute('aria-expanded') === 'true';

  // Calculate and set panel position using the trigger's rect BEFORE the panel
  // is made visible, so there is no post-render jump/move.
  const positionPanel = (trigger, panel) => {
    panel.style.removeProperty('left');

    panel.style.removeProperty('right');

    panel.style.removeProperty('transform');

    panel.style.removeProperty('width');

    const itemEl = trigger.closest('.cb-desktop-navigation-item') || trigger.parentElement;
    const triggerRect = trigger.getBoundingClientRect();
    const itemRect = itemEl.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const PAD = 8;

    if (panel.classList.contains('cb-mega-menu-wide')) {
      // Measure computed col/gap/padding values by briefly rendering the panel
      // off-screen (visibility:hidden + position:fixed keeps it out of flow but
      // allows the browser to lay it out so we can read real pixel values).
      measuringPanel = true;

      panel.style.visibility = 'hidden';

      panel.style.position = 'fixed';

      panel.style.left = '-9999px';

      panel.removeAttribute('hidden');

      const colEls = panel.querySelectorAll('.cb-mega-menu-col');
      const gridEl = panel.querySelector('.cb-mega-menu-grid');
      // Sum each column's real sub-pixel width (ceil to avoid fractional shortfall)
      let totalColW = 0;
      colEls.forEach(col => {
        totalColW += Math.ceil(col.getBoundingClientRect().width);
      });
      const gap = gridEl ? parseFloat(getComputedStyle(gridEl).columnGap) || 0 : 0;
      const padH = parseFloat(getComputedStyle(panel).paddingLeft) + parseFloat(getComputedStyle(panel).paddingRight);

      panel.setAttribute('hidden', '');

      panel.style.removeProperty('visibility');

      panel.style.removeProperty('position');

      measuringPanel = false;
      // 'left' will be overwritten below

      // Width = sum of all col widths + gaps between them + horizontal padding + 6px safety margin
      const calculated = totalColW + Math.max(0, colEls.length - 1) * gap + padH + 6;
      const panelW = Math.min(calculated, vw - 2 * PAD);
      panel.style.width = panelW + 'px';

      // Center on the trigger midpoint, clamped inside the viewport.
      const leftVP = Math.max(PAD, Math.min(triggerRect.left + triggerRect.width / 2 - panelW / 2, vw - PAD - panelW));
      panel.style.left = leftVP - itemRect.left + 'px';

      panel.style.transform = 'none'; // cancel CSS translateX(-50%)
    } else {
      // Small/simple panel: measure real width off-screen, then decide whether
      // to align with the item's left edge (left:0) or right edge (right:0).
      measuringPanel = true;

      panel.style.visibility = 'hidden';

      panel.style.position = 'fixed';

      panel.style.left = '-9999px';

      panel.removeAttribute('hidden');

      const panelW = Math.ceil(panel.getBoundingClientRect().width);

      panel.setAttribute('hidden', '');

      panel.style.removeProperty('visibility');

      panel.style.removeProperty('position');

      measuringPanel = false;

      // itemRect.left is where the panel's left edge would land in the viewport.
      // If right edge would exceed the safe viewport boundary, flip to right-align.
      if (itemRect.left + panelW > vw - PAD) {
        panel.style.left = 'auto';

        panel.style.right = '0';
      } else {
        panel.style.left = '0';

        panel.style.right = 'auto';
      }
    }

    // Clamp height so the panel never extends closer than 24px to the bottom of the viewport.
    const maxH = window.innerHeight - itemRect.bottom - 24;
    panel.style.maxHeight = Math.max(100, maxH) + 'px';

    panel.style.overflowY = 'auto';
  };

  const closePanel = trigger => {
    const panel = panelFor(trigger);
    if (!panel) return;
    trigger.setAttribute('aria-expanded', 'false');

    panel.setAttribute('hidden', '');

    // Collapse any open accordions inside the panel
    panel.querySelectorAll('.cb-mega-more-accordion-trigger[aria-expanded="true"]').forEach(acc => {
      acc.setAttribute('aria-expanded', 'false');
      const accPanel = elem.querySelector(`[id="${acc.getAttribute('aria-controls')}"]`);
      if (accPanel) accPanel.setAttribute('hidden', '');
    });

    panel.style.removeProperty('left');

    panel.style.removeProperty('right');

    panel.style.removeProperty('transform');

    panel.style.removeProperty('width');

    panel.style.removeProperty('max-height');

    panel.style.removeProperty('overflow-y');
  };

  const focusFirstInPanel = panel => {
    const first = panel.querySelector('a, button, [tabindex="0"]');
    if (first) first.focus();
  };

  const closeAll = (except = null) => {
    elem.querySelectorAll('.cb-desktop-navigation-trigger').forEach(t => {
      if (t !== except) closePanel(t);
    });
  };

  const openPanel = trigger => {
    const panel = panelFor(trigger);
    if (!panel) return;
    closeAll(trigger);

    positionPanel(trigger, panel); // position before making visible — no jump

    trigger.setAttribute('aria-expanded', 'true');

    panel.removeAttribute('hidden');
    if (lastInputType === 'keyboard') focusFirstInPanel(panel);
    // Re-apply selection so any accordion containing the selected item is opened
    applySelection();
  };

  // ── More dropdown ──────────────────────────────────────────────────────────

  const createMoreItem = () => {
    moreItem = document.createElement('li');

    moreItem.className = 'cb-desktop-navigation-item cb-desktop-navigation-item-has-menu cb-procedural';

    moreItem.setAttribute('hidden', '');

    moreTrigger = document.createElement('button');

    moreTrigger.className = 'cb-desktop-navigation-trigger';

    moreTrigger.setAttribute('type', 'button');

    moreTrigger.setAttribute('aria-expanded', 'false');
    const moreId = uniqueID();
    moreTrigger.setAttribute('aria-controls', moreId);

    moreTrigger.innerHTML =
      'More <i class="cb-icon cb-down" aria-hidden="true"></i>' +
      '<i class="cb-icon cb-up cb-hidden" aria-hidden="true"></i>';

    const morePanel = document.createElement('div');
    morePanel.className = 'cb-mega-menu cb-mega-menu-more';

    morePanel.id = moreId;

    morePanel.setAttribute('hidden', '');

    moreGrid = document.createElement('ul');

    moreGrid.className = 'cb-mega-menu-more-list';

    morePanel.appendChild(moreGrid);

    moreItem.appendChild(moreTrigger);

    moreItem.appendChild(morePanel);

    navListElem.appendChild(moreItem);
  };

  const getTriggerLabel = li => {
    const trigger = li.querySelector('.cb-desktop-navigation-trigger');
    if (trigger) {
      let text = '';
      trigger.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) text += node.textContent;
      });

      text = text.trim();
      // Fall back to first <span> when the label is wrapped (e.g. <span>Students</span>)
      if (!text) {
        const span = trigger.querySelector('span');
        if (span) text = span.textContent.trim();
      }

      return text;
    }
    const link = li.querySelector('.cb-menu-link');

    return link ? link.textContent.trim() : '';
  };

  // Clone a nav link for use in the More panel:
  // - keeps only the text (strips icons / nested elements)
  // - sets class to cb-mega-menu-link only
  const cloneCleanLink = a => {
    const clean = document.createElement('a');
    clean.href = a.getAttribute('href') || '#';

    clean.className = 'cb-mega-menu-link';
    // Extract plain text — skip icon elements (<i>, <span> with cb-icon)
    let text = '';
    a.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node;
        // Skip icon elements
        if (el.tagName === 'I' || el.classList.contains('cb-icon')) return;
        // Unwrap spans that wrap the label text
        text += el.textContent;
      }
    });

    clean.textContent = text.trim();

    return clean;
  };

  const addToMore = li => {
    li.setAttribute('data-cb-mega-more', 'true');

    li.setAttribute('hidden', '');

    let megaId = li.getAttribute('data-mega-id');
    if (!megaId) {
      megaId = uniqueID();

      li.setAttribute('data-mega-id', megaId);
    }

    // Skip if already represented in More
    if (moreGrid.querySelector(`[data-mega-col="${megaId}"]`)) return;

    const label = getTriggerLabel(li);
    const panel = li.querySelector('.cb-mega-menu');

    // Build a section for this nav item as a single <li> in the big list
    const col = document.createElement('li');
    col.className = 'cb-mega-menu-more-section';

    col.setAttribute('data-mega-col', megaId);

    const isWide = panel && panel.classList.contains('cb-mega-menu-wide');

    // Section heading (non-wide menus with a panel)
    let headingId = null;
    if (li.classList.contains('cb-desktop-navigation-item-has-menu') && !isWide) {
      const heading = document.createElement('h3');
      heading.className = 'cb-mega-menu-more-heading';

      headingId = uniqueID();

      heading.id = headingId;

      heading.textContent = label;

      col.appendChild(heading);
    }

    if (isWide) {
      // Wide menu: the nav label is the single accordion trigger.
      // Inside: landing page link first, then all column links flat.
      const accId = uniqueID();
      const triggerId = uniqueID();

      const accTrigger = document.createElement('a');
      accTrigger.className = 'cb-mega-more-accordion-trigger';

      accTrigger.id = triggerId;

      accTrigger.href = '#';

      accTrigger.setAttribute('aria-expanded', 'false');

      accTrigger.setAttribute('aria-controls', accId);

      accTrigger.innerHTML =
        '<i class="cb-icon cb-down" aria-hidden="true"></i>' +
        '<i class="cb-icon cb-up cb-hidden" aria-hidden="true"></i>' +
        `<span>${label}</span>`;

      const accPanel = document.createElement('ul');
      accPanel.className = 'cb-mega-more-accordion-panel';

      accPanel.id = accId;

      accPanel.setAttribute('hidden', '');

      accPanel.setAttribute('aria-labelledby', triggerId);

      // Landing page link first
      const wideTrigger = li.querySelector('.cb-desktop-navigation-trigger');
      if (wideTrigger && wideTrigger.getAttribute('data-cb-href')) {
        const landingLi = document.createElement('li');

        const landingA = document.createElement('a');
        landingA.href = wideTrigger.getAttribute('data-cb-href');

        landingA.className = 'cb-mega-menu-link';

        landingA.textContent = `${label} ${data.landingPageSuffix}`;

        landingLi.appendChild(landingA);

        accPanel.appendChild(landingLi);
      }

      // Keep each source column as its own UI group in the More accordion panel.
      panel.querySelectorAll('.cb-mega-menu-col').forEach(srcCol => {
        const srcHeading = srcCol.querySelector('.cb-mega-menu-col-heading');
        const links = srcCol.querySelectorAll('.cb-mega-menu-link');
        if (!links.length) return;

        const columnGroupLi = document.createElement('li');
        columnGroupLi.className = 'cb-mega-more-column-group';

        const columnGroupList = document.createElement('ul');
        columnGroupList.className = 'cb-mega-more-column-list';

        if (srcHeading) {
          // Extract text-only content — skip icon <span>/<i> elements
          let headingText = '';
          srcHeading.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
              headingText += node.textContent;
            } else if (node.nodeType === Node.ELEMENT_NODE && !node.classList.contains('cb-icon')) {
              headingText += node.textContent;
            }
          });

          headingText = headingText.trim();
          if (headingText) {
            const colHeadingId = uniqueID();
            const h4 = document.createElement('h4');
            h4.className = 'cb-mega-menu-col-heading';

            h4.id = colHeadingId;

            h4.textContent = headingText;

            columnGroupLi.appendChild(h4);

            columnGroupList.setAttribute('aria-labelledby', colHeadingId);
          }
        }

        links.forEach(a => {
          const linkLi = document.createElement('li');
          linkLi.appendChild(cloneCleanLink(a));

          columnGroupList.appendChild(linkLi);
        });

        columnGroupLi.appendChild(columnGroupList);

        accPanel.appendChild(columnGroupLi);
      });

      col.appendChild(accTrigger);

      col.appendChild(accPanel);
    } else if (panel) {
      const smList = document.createElement('ul');
      smList.className = 'cb-mega-menu-more-links';

      if (headingId) {
        smList.setAttribute('aria-labelledby', headingId);
      }

      const cols = panel.querySelectorAll('.cb-mega-menu-col');
      if (cols.length) {
        // Multi-column panel (cb-mega-menu-sm): each original column becomes an accordion section
        cols.forEach(srcCol => {
          const srcHeading = srcCol.querySelector('.cb-mega-menu-col-heading');
          const links = srcCol.querySelectorAll('.cb-mega-menu-link');
          if (!links.length) return;

          if (!srcHeading) {
            // No heading — render links as list items
            links.forEach(a => {
              const linkLi = document.createElement('li');
              linkLi.appendChild(cloneCleanLink(a));

              smList.appendChild(linkLi);
            });

            return;
          }

          const accLi = document.createElement('li');

          const accId = uniqueID();
          const triggerId = uniqueID();

          const accTrigger = document.createElement('a');
          accTrigger.className = 'cb-mega-more-accordion-trigger';

          accTrigger.id = triggerId;

          accTrigger.href = '#';

          accTrigger.setAttribute('aria-expanded', 'false');

          accTrigger.setAttribute('aria-controls', accId);

          accTrigger.innerHTML =
            '<i class="cb-icon cb-down" aria-hidden="true"></i>' +
            '<i class="cb-icon cb-up cb-hidden" aria-hidden="true"></i>' +
            `<span>${srcHeading.textContent}</span>`;

          const accPanel = document.createElement('ul');
          accPanel.className = 'cb-mega-more-accordion-panel';

          accPanel.id = accId;

          accPanel.setAttribute('hidden', '');

          accPanel.setAttribute('aria-labelledby', triggerId);

          links.forEach(a => {
            const linkLi = document.createElement('li');
            linkLi.setAttribute('aria-labelledby', triggerId);

            linkLi.appendChild(cloneCleanLink(a));

            accPanel.appendChild(linkLi);
          });

          accLi.appendChild(accTrigger);

          accLi.appendChild(accPanel);

          smList.appendChild(accLi);
        });
      } else {
        // Simple panel: flat list of links
        panel.querySelectorAll('.cb-mega-menu-link').forEach(a => {
          const linkLi = document.createElement('li');
          linkLi.appendChild(cloneCleanLink(a));

          smList.appendChild(linkLi);
        });
      }

      // Prepend landing-page link for non-wide menus that have data-cb-href
      const itemTrigger = li.querySelector('.cb-desktop-navigation-trigger');
      if (itemTrigger && itemTrigger.getAttribute('data-cb-href')) {
        const landingLi = document.createElement('li');

        const landingA = document.createElement('a');
        landingA.href = itemTrigger.getAttribute('data-cb-href');

        landingA.className = 'cb-mega-menu-link';

        landingA.textContent = `${label} ${data.landingPageSuffix}`;

        landingLi.appendChild(landingA);

        smList.insertBefore(landingLi, smList.firstChild);
      }

      col.appendChild(smList);
    } else {
      // Plain link — single entry
      const a = li.querySelector('.cb-menu-link');
      if (a) {
        col.appendChild(cloneCleanLink(a));
      }
    }

    // Prepend so items appear in original nav order (overflowing items are added right-to-left)
    moreGrid.insertBefore(col, moreGrid.firstChild);

    moreItem.removeAttribute('hidden');
    if (data.callBackMore) data.callBackMore(megaId, 1);
  };

  const removeFromMore = li => {
    li.removeAttribute('data-cb-mega-more');

    li.removeAttribute('hidden');

    const megaId = li.getAttribute('data-mega-id');
    if (!megaId) return;

    const col = moreGrid.querySelector(`[data-mega-col="${megaId}"]`);
    if (col) col.remove();
    if (data.callBackMore) data.callBackMore(megaId, 0);

    if (moreGrid.children.length === 0) {
      moreItem.setAttribute('hidden', '');

      closePanel(moreTrigger);
    }
  };

  // Show or hide the More button depending on whether it has any columns.
  // Also reapplies selected state so the correct visible link is marked after
  // items move in/out of the More panel.
  const adjustMoreDisplay = () => {
    if (moreGrid.children.length > 0) {
      moreItem.removeAttribute('hidden');
    } else {
      moreItem.setAttribute('hidden', '');

      closePanel(moreTrigger);
    }
    if (selectedHref) applySelection();
  };

  // Measure widths once at init (mirrors LocalNavigation getWidths).
  // Stores brand outerWidth (sWidth), More button outerWidth (moreItemWidth),
  // and each item's natural outerWidth in data-mega-width.
  // Note: +2 added to each offsetWidth as a subpixel-rounding safety margin.
  const getWidths = () => {
    const brandEl = elem.querySelector('.cb-desktop-navigation-brand');
    if (brandEl) sWidth = brandEl.offsetWidth + 2;

    moreItem.style.visibility = 'hidden';

    moreItem.removeAttribute('hidden');

    moreItemWidth = moreItem.offsetWidth + 2;

    moreItem.setAttribute('hidden', '');

    moreItem.style.removeProperty('visibility');

    navListElem.querySelectorAll(':scope > .cb-desktop-navigation-item').forEach(li => {
      if (li === moreItem) return;
      li.setAttribute('data-mega-width', li.offsetWidth + 2);
    });
  };

  const trackOverflow = () => {
    // Available width for all flex items (brand + nav list + more button).
    // innerEl.offsetWidth = full column width; subtract its horizontal padding.
    // Mirrors: dWidth = apricot.Utils.width(desktopNav) - 48
    const innerEl = elem.querySelector('.cb-desktop-navigation-inner') || elem;
    const innerStyle = getComputedStyle(innerEl);
    const innerPad = parseFloat(innerStyle.paddingLeft) + parseFloat(innerStyle.paddingRight);
    const dWidth = innerEl.offsetWidth - innerPad;

    // Space available strictly for the nav list (minus the brand footprint).
    // Mirrors: nWith = dWidth - sWidth
    const nWith = dWidth - sWidth;

    // Limit changes based on whether More is currently visible in the DOM.
    // Mirrors LocalNavigation: hasClass(more, 'cb-hidden') ? nWith : nWith - mWidth
    const limit = moreItem.hasAttribute('hidden') ? nWith : nWith - moreItemWidth;

    let actualWidth = 0;
    const toAdd = [];

    navListElem.querySelectorAll(':scope > .cb-desktop-navigation-item').forEach(li => {
      if (li === moreItem) return;

      actualWidth += parseInt(li.getAttribute('data-mega-width') || '0', 10);

      if (actualWidth >= limit) {
        // Overflows — add to More (on first run always; after that, only if not already there)
        if (firstOverflowRun || !li.hasAttribute('data-cb-mega-more')) {
          toAdd.push(li);
        }
      } else {
        // Fits — pull back out of More if it was there
        if (li.hasAttribute('data-cb-mega-more')) removeFromMore(li);
      }
    });

    if (toAdd.length > 0) {
      toAdd.reverse();

      toAdd.forEach(li => addToMore(li));
    }

    adjustMoreDisplay();
  };

  // ── Mobile panel logic ─────────────────────────────────────────────────────

  const lockBody = () => document.body.classList.add('cb-desktop-navigation-locked');
  const unlockBody = () => document.body.classList.remove('cb-desktop-navigation-locked');

  const closeMobileNav = () => {
    // Instantly hide all panels without animation
    mobileNav.querySelectorAll('.cb-site-mobile-panel').forEach(p => {
      p.classList.remove('cb-is-active');

      p.setAttribute('aria-hidden', 'true');
    });

    // Remove dynamically-created child panels (depth > 0)
    mobileNav.querySelectorAll('.cb-site-mobile-panel[data-mobile-depth]').forEach(p => {
      if (parseInt(p.getAttribute('data-mobile-depth'), 10) > 0) p.remove();
    });

    mobilePanelStack = [];

    mobileNav.classList.remove('cb-is-open');

    mobileNav.setAttribute('aria-hidden', 'true');

    mobileTrigger.setAttribute('aria-expanded', 'false');

    unlockBody();

    mobileTrigger.focus();

    if (data.callBackMobileClose) data.callBackMobileClose();
  };

  const closeMobilePanel = () => {
    const panel = mobilePanelStack.pop();
    if (!panel) return;
    panel.classList.remove('cb-is-active');

    panel.setAttribute('aria-hidden', 'true');
    // Remove child panels (depth > 0) from DOM after transition
    const depth = parseInt(panel.getAttribute('data-mobile-depth'), 10);
    if (depth > 0) {
      let removed = false;
      const cleanup = () => {
        if (!removed) {
          removed = true;

          panel.remove();
        }
      };
      panel.addEventListener('transitionend', cleanup, { once: true });

      setTimeout(cleanup, PANEL_CLEANUP_MS);
    }
    // Restore focus to the parent link that opened this panel, or fall back to the mobile trigger
    const focusTarget = panel._parentLink || mobileTrigger;
    if (focusTarget) setTimeout(() => focusTarget.focus(), FOCUS_DELAY_MS);
  };
  const openMobilePanel = panel => {
    panel.setAttribute('aria-hidden', 'false');

    // Force reflow so the CSS transition plays
    panel.getBoundingClientRect();

    panel.classList.add('cb-is-active');

    mobilePanelStack.push(panel);
    // Move focus to first focusable element in panel
    const first = panel.querySelector('a, button');
    if (first) setTimeout(() => first.focus(), FOCUS_DELAY_MS);
  };
  // Build a single slide panel element from an array of item descriptors.
  // Each descriptor: { label, href, children: [] }
  // children present → renders as a "drill-in" row; absent → plain link.
  const buildPanel = (items, title, depth) => {
    const panel = document.createElement('div');
    panel.className = 'cb-site-mobile-panel';

    panel.setAttribute('aria-hidden', 'true');

    panel.setAttribute('data-mobile-depth', depth);

    // Header row
    const header = document.createElement('div');
    header.className = 'cb-site-mobile-panel-header';

    if (depth > 0) {
      const backBtn = document.createElement('button');
      backBtn.className = 'cb-site-mobile-panel-back';

      backBtn.setAttribute('type', 'button');

      backBtn.innerHTML = '<span class="cb-icon cb-left cb-margin-right-8" aria-hidden="true"></span> Back';

      backBtn.addEventListener('click', () => {
        closeMobilePanel();
      });

      header.appendChild(backBtn);
    }

    if (title) {
      const titleEl = document.createElement('span');
      titleEl.className = 'cb-site-mobile-panel-title';

      titleEl.textContent = title;

      header.appendChild(titleEl);
    }

    const closeBtn = document.createElement('button');
    closeBtn.className = 'cb-site-mobile-panel-close';

    closeBtn.setAttribute('type', 'button');

    closeBtn.innerHTML =
      '<i class="cb-icon cb-x-mark" aria-hidden="true"></i><span class="sr-only">Close Navigation Panels</span>';

    closeBtn.addEventListener('click', () => {
      closeMobileNav();
    });

    header.appendChild(closeBtn);

    panel.appendChild(header);

    // Link list
    const ul = document.createElement('ul');
    ul.className = 'cb-site-mobile-panel-list';

    // Track current section group so links following a heading are nested inside it
    let currentGroupUl = null;

    items.forEach(item => {
      if (item.isSectionHeading) {
        const groupLi = document.createElement('li');
        const h3 = document.createElement('h3');
        h3.className = 'cb-site-mobile-panel-section-heading';

        const headingId = item.id || uniqueID();

        h3.id = headingId;

        h3.textContent = item.label;

        groupLi.appendChild(h3);

        currentGroupUl = document.createElement('ul');

        currentGroupUl.setAttribute('aria-labelledby', headingId);

        groupLi.appendChild(currentGroupUl);

        ul.appendChild(groupLi);

        return;
      }

      const li = document.createElement('li');
      const a = document.createElement('a');

      a.className = 'cb-site-mobile-panel-link';

      // if (item.isPromo) a.classList.add('cb-site-mobile-panel-link-promo');
      a.href = item.href || '#';
      if (item.children && item.children.length) {
        a.classList.add('cb-site-mobile-panel-link-has-children');

        // Store children so applySelection can evaluate containsHref without
        // needing the child panel to be open.
        a._navItemChildren = item.children;

        a.addEventListener('click', e => {
          e.preventDefault();
          // Build child panel lazily and open it
          const childPanel = buildPanel(item.children, item.label, depth + 1);
          childPanel._parentLink = a; // store ref so applySelection can walk the ancestor chain

          mobileNav.appendChild(childPanel);

          openMobilePanel(childPanel);

          // Re-sweep now that the child panel links exist in the DOM
          applySelection();
        });
      }
      if (item.children && item.children.length) {
        const span = document.createElement('span');
        span.textContent = item.label;

        a.appendChild(span);
        const icon = document.createElement('i');
        icon.className = 'cb-icon cb-right';

        icon.setAttribute('aria-hidden', 'true');

        a.appendChild(icon);
        const srSpan = document.createElement('span');
        srSpan.className = 'sr-only';

        srSpan.id = uniqueID();

        srSpan.textContent = 'opens sub navigation group';

        a.appendChild(srSpan);
      } else {
        a.textContent = item.label;

        a.addEventListener('click', e => {
          if (!item.isPromo) setSelected(a);
          if (data.callBackMobile) data.callBackMobile(e, { label: item.label, href: item.href });
          if (data.callBack) data.callBack(e, { label: item.label, href: item.href });
          if (data.mobileInteractionClose) closeMobileNav();
        });
      }
      li.appendChild(a);

      // Append to current section group if inside one, otherwise to the top-level list
      if (currentGroupUl) {
        currentGroupUl.appendChild(li);
      } else {
        ul.appendChild(li);
      }
    });

    panel.appendChild(ul);

    return panel;
  };

  // Extract visible text from an element, skipping icon children (<i> or elements with cb-icon class)
  const getCleanText = el => {
    let text = '';
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === 'I' || node.classList.contains('cb-icon')) return;
        text += node.textContent;
      }
    });

    return text.trim();
  };

  // Collect nav item descriptors from the live desktop markup.
  const collectNavItems = () => {
    const items = [];

    navListElem.querySelectorAll(':scope > .cb-desktop-navigation-item').forEach(li => {
      if (li === moreItem) return;

      const megaPanel = li.querySelector('.cb-mega-menu');
      const link = li.querySelector('.cb-menu-link');

      if (megaPanel) {
        // Trigger with a mega panel — build children from columns
        const label = getTriggerLabel(li);

        const children = [];

        // Multi-column (grid) panel
        const cols = megaPanel.querySelectorAll('.cb-mega-menu-col');
        if (cols.length) {
          cols.forEach(col => {
            const heading = col.querySelector('.cb-mega-menu-col-heading');
            if (heading) {
              children.push({ isSectionHeading: true, label: getCleanText(heading), id: heading.id || null });
            }
            const isPromo = col.classList.contains('cb-mega-menu-link-promo');
            col.querySelectorAll('.cb-mega-menu-link').forEach(a => {
              children.push({ label: getCleanText(a), href: a.getAttribute('href'), isPromo });
            });
          });
        } else {
          // Simple list panel
          megaPanel.querySelectorAll('.cb-mega-menu-link').forEach(a => {
            children.push({ label: getCleanText(a), href: a.getAttribute('href') });
          });
        }

        items.push({ label, children });
      } else if (link) {
        // Plain link
        items.push({ label: getCleanText(link), href: link.getAttribute('href') });
      }
    });

    return items;
  };

  const openMobileNav = () => {
    mobileNav.classList.add('cb-is-open');

    mobileNav.setAttribute('aria-hidden', 'false');

    mobileTrigger.setAttribute('aria-expanded', 'true');

    lockBody();

    closeAll(); // close any open desktop panels

    // Always rebuild the root panel so the containsHref check in buildPanel
    // uses the current selectedHref (it may have changed since the last open).
    const existingRoot = mobileNav.querySelector('.cb-site-mobile-panel');
    if (existingRoot) existingRoot.remove();

    const rootItems = collectNavItems();
    const rootPanel = buildPanel(rootItems, '', 0);
    mobileNav.appendChild(rootPanel);

    mobilePanelStack = [];

    openMobilePanel(rootPanel);

    // Sweep all newly built root-panel links with the current selection
    applySelection();

    if (data.callBackMobileOpen) data.callBackMobileOpen();
  };

  const onPointerDown = () => {
    lastInputType = 'mouse';
  };
  const onKeyboardFirst = () => {
    lastInputType = 'keyboard';
  };

  const handleResize = () => {
    // Close any open desktop panel (including More) on every resize
    closeAll();
    // Sync mobile-layout class on container
    if (navContainer && data.mobileLayout !== false) {
      navContainer.classList.toggle('cb-local-navigation-mega-mobile', window.innerWidth <= MOBILE_BP);
    }

    if (!data.mobileLayout) return;
    if (mobileTrigger) {
      const visible = window.getComputedStyle(mobileTrigger).display !== 'none';
      mobileTrigger.setAttribute('tabindex', visible ? '0' : '-1');

      mobileTrigger.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
    clearTimeout(mobileResizeId);

    mobileResizeId = setTimeout(() => {
      if (window.innerWidth > MOBILE_BP && mobileNav && mobileNav.classList.contains('cb-is-open')) {
        closeMobileNav();
      }
    }, 200);
  };

  const initMobile = () => {
    if (!data.mobileLayout) return;
    mobileTrigger = elem.querySelector('.cb-desktop-navigation-mobile-trigger');

    mobileNav = elem.querySelector('.cb-mobile-navigation');
    if (!mobileTrigger || !mobileNav) return;

    const setMobileTriggerA11y = () => {
      const visible = window.getComputedStyle(mobileTrigger).display !== 'none';
      mobileTrigger.setAttribute('tabindex', visible ? '0' : '-1');

      mobileTrigger.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    setMobileTriggerA11y();

    const onMobileTriggerClick = () => {
      if (mobileTrigger.getAttribute('aria-expanded') === 'true') {
        closeMobileNav();
      } else {
        openMobileNav();
      }
    };
    mobileTrigger.addEventListener('click', onMobileTriggerClick);

    mobileTrigger._megaMenuClickHandler = onMobileTriggerClick;

    window.addEventListener('resize', handleResize);
  };

  // ── Event handlers ─────────────────────────────────────────────────────────

  const toggleAccordion = trigger => {
    const panel = elem.querySelector(`[id="${trigger.getAttribute('aria-controls')}"]`);
    if (!panel) return;
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));

    // expanded ? panel.setAttribute('hidden', '') : panel.removeAttribute('hidden');
    if (expanded) {
      panel.setAttribute('hidden', '');
    } else {
      panel.removeAttribute('hidden');
    }
  };

  const handleClick = e => {
    // Accordion triggers inside the More panel
    const accTrigger = e.target.closest('.cb-mega-more-accordion-trigger');
    if (accTrigger) {
      e.preventDefault();

      e.stopPropagation();

      toggleAccordion(accTrigger);

      return;
    }
    // Desktop mega-menu link click (including More panel)
    const desktopLink = e.target.closest('.cb-mega-menu-link');
    if (desktopLink) {
      const isPromoLink = !!desktopLink.closest('.cb-mega-menu-link-promo');
      if (!isPromoLink) setSelected(desktopLink);
      if (!desktopLink.closest('.cb-mega-menu-more')) {
        if (data.callBackDesktop) data.callBackDesktop(e, desktopLink);
      }
      if (data.callBack)
        data.callBack(e, { label: desktopLink.textContent.trim(), href: desktopLink.getAttribute('href') });
      if (data.dropdownCloseOnClick !== false) closeAll();

      return;
    }
    // Desktop plain nav link (e.g. Home)
    const plainNavLink = e.target.closest('.cb-menu-link');
    if (plainNavLink) {
      setSelected(plainNavLink);
      if (data.callBack)
        data.callBack(e, { label: plainNavLink.textContent.trim(), href: plainNavLink.getAttribute('href') });
      if (data.dropdownCloseOnClick !== false) closeAll();

      return;
    }
    const trigger = e.target.closest('.cb-desktop-navigation-trigger');
    if (!trigger) return;
    e.preventDefault();

    e.stopPropagation();

    if (isOpen(trigger)) {
      closePanel(trigger);
    } else {
      openPanel(trigger);
    }
  };

  const handleKeydown = e => {
    // ── Mobile focus trap ───────────────────────────────────────────────────
    if (mobileNav && mobileNav.classList.contains('cb-is-open')) {
      const activePanel = mobilePanelStack[mobilePanelStack.length - 1];
      if (activePanel) {
        const focusable = Array.from(activePanel.querySelectorAll('a, button, [tabindex="0"]')).filter(
          el => !el.hasAttribute('disabled'),
        );

        if (e.key === 'Tab') {
          if (!focusable.length) {
            e.preventDefault();

            return;
          }
          e.preventDefault();
          const idx = focusable.indexOf(document.activeElement);
          if (e.shiftKey) {
            // rotate backwards
            const prev = idx <= 0 ? focusable[focusable.length - 1] : focusable[idx - 1];
            prev.focus();
          } else {
            // rotate forwards
            const next = idx >= focusable.length - 1 ? focusable[0] : focusable[idx + 1];
            next.focus();
          }

          return;
        }

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          if (!focusable.length) {
            e.preventDefault();

            return;
          }
          e.preventDefault();
          const idx = focusable.indexOf(document.activeElement);
          if (e.key === 'ArrowDown') {
            if (idx < focusable.length - 1) focusable[idx + 1].focus();
          } else {
            if (idx > 0) focusable[idx - 1].focus();
          }

          return;
        }
      }
    }
    // ── /Mobile focus trap ──────────────────────────────────────────────────

    if (e.key === 'Escape') {
      // Close mobile nav if open
      if (data.mobileEscClose && mobileNav && mobileNav.classList.contains('cb-is-open')) {
        closeMobileNav();

        return;
      }
      // Close desktop panel
      const open = elem.querySelector('.cb-desktop-navigation-trigger[aria-expanded="true"]');
      if (open) {
        closePanel(open);

        open.focus();
      }

      return;
    }

    if (e.key === ' ') {
      const accTrigger = e.target.closest('.cb-mega-more-accordion-trigger');
      if (accTrigger) {
        e.preventDefault();

        toggleAccordion(accTrigger);

        return;
      }
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const trigger = e.target.closest('.cb-desktop-navigation-trigger');

      // If focus is on a trigger, open the panel (focus moves automatically)
      if (trigger) {
        e.preventDefault();
        if (!isOpen(trigger)) {
          openPanel(trigger);
        } else {
          // Panel already open — move into it
          const panel = panelFor(trigger);
          if (panel) {
            const links = Array.from(panel.querySelectorAll('a'));
            const target = e.key === 'ArrowDown' ? links[0] : links[links.length - 1];
            if (target) target.focus();
          }
        }

        return;
      }

      // If focus is already inside an open panel, move between links
      const openMenuPanel = elem.querySelector('.cb-mega-menu:not([hidden])');
      if (openMenuPanel && openMenuPanel.contains(document.activeElement)) {
        e.preventDefault();
        const links = Array.from(openMenuPanel.querySelectorAll('a'));
        const idx = links.indexOf(document.activeElement);
        if (idx === -1) return;
        if (e.key === 'ArrowDown') {
          const next = links[idx + 1] || links[0]; // wrap to top
          next.focus();
        } else {
          const prev = links[idx - 1] || links[links.length - 1]; // wrap to bottom
          prev.focus();
        }
      }
    }
  };

  // ── A11Y ──────────────────────────────────────────────────────────────────
  // Ensure every trigger ↔ panel pair is properly delineated:
  // • trigger gets an id (if missing)
  // • panel gets role="region" + aria-labelledby pointing at the trigger id
  const applyPanelA11y = () => {
    elem.querySelectorAll('.cb-desktop-navigation-trigger[aria-controls]').forEach(trigger => {
      if (!trigger.id) trigger.id = uniqueID();
      const panel = panelFor(trigger);
      if (panel) {
        panel.setAttribute('role', 'region');

        panel.setAttribute('aria-labelledby', trigger.id);
      }
    });

    // Associate each column list with its heading via aria-labelledby
    elem.querySelectorAll('.cb-mega-menu-col').forEach(col => {
      const heading = col.querySelector('.cb-mega-menu-col-heading');
      const list = col.querySelector('.cb-mega-menu-col-list');
      if (heading && list) {
        if (!heading.id) heading.id = uniqueID();
        list.setAttribute('aria-labelledby', heading.id);
      }
    });
  };

  const handleOutside = e => {
    if (!elem.contains(e.target)) closeAll();
  };

  const handleFocusOut = () => {
    setTimeout(() => {
      const focused = document.activeElement;
      // Close each open panel whose trigger+panel subtree no longer contains focus
      elem.querySelectorAll('.cb-desktop-navigation-trigger[aria-expanded="true"]').forEach(trigger => {
        const item = trigger.closest('.cb-desktop-navigation-item');
        const panel = panelFor(trigger);
        const inItem = item && item.contains(focused);
        const inPanel = panel && panel.contains(focused);
        if (!inItem && !inPanel) closePanel(trigger);
      });
    }, 0);
  };

  // ── Landing-page link injection ────────────────────────────────────────────
  // For each .cb-desktop-navigation-trigger that carries a data-cb-href attribute,
  // if the associated mega panel is a small panel (cb-mega-menu-sm), prepend a
  // landing-page <li> as the very first item so users can navigate to the section
  // root directly from the dropdown.
  const injectLandingPageLinks = () => {
    navListElem.querySelectorAll('.cb-desktop-navigation-trigger[data-cb-href]').forEach(trigger => {
      const panel = panelFor(trigger);
      if (!panel) return;

      const href = trigger.getAttribute('data-cb-href');
      const label = getTriggerLabel(trigger.closest('.cb-desktop-navigation-item'));
      const a = document.createElement('a');
      a.href = href;

      a.className = 'cb-mega-menu-link';

      a.textContent = `${label} ${data.landingPageSuffix}`;
      if (panel.classList.contains('cb-mega-menu-sm')) {
        const li = document.createElement('li');

        li.appendChild(a);

        const ul = panel.querySelector('ul');
        if (ul) {
          ul.insertBefore(li, ul.firstChild);
        }
      } else if (panel.classList.contains('cb-mega-menu-wide')) {
        const div = document.createElement('div');
        div.className = 'cb-mega-menu-landing-page';

        div.appendChild(a);
        const grid = panel.querySelector('.cb-mega-menu-grid');
        if (grid) {
          panel.insertBefore(div, grid);
        }
      }
    });
  };

  // ── Init / Destroy ──────────────────────────────────────────────────────────

  const init = () => {
    elem.localNavPlugin = 'cb';

    navListElem = elem.querySelector('.cb-desktop-navigation-list');
    if (!navListElem) return;

    // Apply modifiers to the container (wraps both nav + mobile overlay)
    navContainer = elem.closest('.cb-local-navigation-mega') || elem;
    const container = navContainer;
    container.classList.toggle('cb-local-navigation-mega-sticky', data.sticky !== false);

    container.classList.toggle('cb-local-navigation-mega-no-mobile', data.mobileLayout === false);
    if (data.mobileLayout !== false) {
      container.classList.toggle('cb-local-navigation-mega-mobile', window.innerWidth <= MOBILE_BP);
    }

    // Auto-generate panel IDs and wire aria-controls on each trigger
    navListElem.querySelectorAll(':scope > .cb-desktop-navigation-item-has-menu').forEach(item => {
      const trigger = item.querySelector('.cb-desktop-navigation-trigger');
      const panel = item.querySelector('.cb-mega-menu');
      if (!trigger || !panel) return;
      if (!panel.id) panel.id = uniqueID();
      trigger.setAttribute('aria-controls', panel.id);
    });

    // Inject landing-page links into sm panels for triggers with data-cb-href
    injectLandingPageLinks();

    // For wide panels: if a column has no heading, add top-margin to its list
    elem.querySelectorAll('.cb-mega-menu-wide .cb-mega-menu-col').forEach(col => {
      if (!col.querySelector('.cb-mega-menu-col-heading')) {
        const list = col.querySelector('.cb-mega-menu-col-list');
        if (list) list.classList.add('cb-mega-menu-col-no-heading');
      }
    });

    createMoreItem();

    applyPanelA11y();

    getWidths();

    trackOverflow();

    firstOverflowRun = false;

    initMobile();

    // Apply initial selected state (from data.selectedItem or aria-current already in HTML)
    if (selectedHref) {
      setSelected(selectedHref);
    } else {
      // Respect aria-current="page" already present in static HTML
      // Call setSelected (not just assign) so any duplicate aria-current in HTML gets cleaned up
      const preselected = elem.querySelector('[aria-current="page"]');
      if (preselected) setSelected(preselected.getAttribute('href'));
    }

    // Use ResizeObserver on the inner container so overflow tracking fires
    // whenever the available nav width changes (window resize, zoom, layout shift, etc.)
    const innerEl = elem.querySelector('.cb-desktop-navigation-inner') || elem;
    overflowObserver = new ResizeObserver(() => {
      clearTimeout(resizeId);

      resizeId = setTimeout(() => {
        if (measuringPanel) return;
        closeAll(); // close any open desktop panels; width recalculates on next open

        trackOverflow();
      }, RESIZE_DEBOUNCE_MS);
    });

    overflowObserver.observe(innerEl);

    document.addEventListener('mousedown', onPointerDown);

    document.addEventListener('keydown', onKeyboardFirst, true);

    elem.addEventListener('click', handleClick);

    document.addEventListener('keydown', handleKeydown);

    document.addEventListener('click', handleOutside);

    elem.addEventListener('focusout', handleFocusOut);
  };

  const destroy = () => {
    if (elem.localNavPlugin !== 'cb') return;
    elem.localNavPlugin = null;

    clearTimeout(resizeId);

    clearTimeout(mobileResizeId);
    if (overflowObserver) {
      overflowObserver.disconnect();

      overflowObserver = null;
    }
    if (moreItem && moreItem.parentNode) moreItem.parentNode.removeChild(moreItem);
    moreItem = null;

    moreTrigger = null;

    moreGrid = null;

    window.removeEventListener('resize', handleResize);
    if (mobileTrigger && mobileTrigger._megaMenuClickHandler) {
      mobileTrigger.removeEventListener('click', mobileTrigger._megaMenuClickHandler);

      delete mobileTrigger._megaMenuClickHandler;
    }
    document.removeEventListener('mousedown', onPointerDown);

    document.removeEventListener('keydown', onKeyboardFirst, true);

    elem.removeEventListener('click', handleClick);

    document.removeEventListener('keydown', handleKeydown);

    document.removeEventListener('click', handleOutside);

    elem.removeEventListener('focusout', handleFocusOut);
  };

  if (elem.localNavPlugin !== 'cb') {
    init();
  }

  return {
    destroy: destroy,
    setSelected: setSelected,
  };
};

export default LocalNavigationMegaMenu;
